import {
  customerMarketShopfrontApi,
  marketSearchApi
} from '@envato/foundation-types'

import keys from 'lodash-es/keys'

import {
  getUserCartItems,
  getUserCollections,
  getUserFavoriteCollectionID
} from './user'

import { IItem } from '../../typings/searchResults'
import { IRootState } from '../reducers'

/**
 * Check if an item is favourite
 * @param  {Object} state
 * @param  {Object} item
 * @return {Boolean}
 */
export const isInFavourites = (
  state: IRootState,
  item: customerMarketShopfrontApi.search.Match
) => {
  const collections = getUserCollections(state)
  const favoriteCollectionID = getUserFavoriteCollectionID(state)

  return (
    (collections[favoriteCollectionID] &&
      collections[favoriteCollectionID].bookmarks[item.id] &&
      collections[favoriteCollectionID].bookmarks[item.id].optimisticStatus !==
        'deleted') ||
    false
  )
}

/**
 * Check if an item is in bookmark
 * @param  {Object} state
 * @param  {Object} item
 * @return {Boolean}
 */
export const isInBookmarks = (
  state: IRootState,
  item: customerMarketShopfrontApi.search.Match
) => {
  const collections = getUserCollections(state)

  const nonFavoriteCollections = keys(collections)
    .map(collectionID => collections[collectionID])
    .filter(collection => collection.special_role !== 'favorite')

  return (
    nonFavoriteCollections.some(collection => collection.bookmarks[item.id]) ||
    false
  )
}

/**
 * Check if an item is in cart
 * @param  {Object} state
 * @param  {Object} item
 * @return {Boolean}
 */
export const isInCart = (
  state: IRootState,
  item: customerMarketShopfrontApi.search.Match
) => {
  const cartItems = getUserCartItems(state) as any[]
  return (
    cartItems.some((cartItemId: any) => cartItemId.item_id === item.id) || false
  )
}

export type DecorateSearchResultFunction = (
  state: IRootState,
  match: customerMarketShopfrontApi.search.Match
) => IItem

export const decorateSearchResult: DecorateSearchResultFunction = (
  state,
  match
) => {
  let originalPriceCents

  const activeDiscount = (match.discounts || []).find(isDiscountActive)

  if (
    activeDiscount &&
    match.price_cents < activeDiscount.original_price_cents
  ) {
    originalPriceCents = activeDiscount.original_price_cents
  }

  return {
    ...match,
    ...(originalPriceCents && { original_price_cents: originalPriceCents }),
    ui: {
      isInBookmarks: isInBookmarks(state, match),
      isInCart: isInCart(state, match),
      isInFavourites: isInFavourites(state, match)
    }
  }
}

/**
 * Compute additional property for ease of use in the UI
 * It does not modify any existing props from the API
 * @param  {Object} global redux state
 * @return {Object} the formatted result selected from state
 */
export const searchResults = (state: IRootState) => {
  return {
    ...state.searchPage.results,
    matches: state.searchPage.results.matches.map(match =>
      decorateSearchResult(state, match)
    )
  }
}

const isDiscountActive = (discount: marketSearchApi.IDiscount) => {
  if (!discount.sale_starts_at || !discount.sale_ends_at) {
    return false
  }

  const currentDate = new Date()
  const startDate = new Date(discount.sale_starts_at)
  const endDate = new Date(discount.sale_ends_at)

  return startDate <= currentDate && currentDate < endDate
}
